export function OwnerOccupantRules() {
  const rules = [
    {
      icon: 'ri-home-heart-line',
      title: 'Principal Residence Requirement',
      description: 'You must live in the property as your primary residence (183+ days/year). Vancouver enforces BC\'s Provincial STR Act requiring principal residence occupancy. Both owners and long-term tenants can host, provided they meet all requirements.',
      details: [
        'Must be your primary home where you live 183+ days/year',
        'Can include one secondary suite or ADU',
        'Provincial Registry Number required',
        'Both owners and tenants eligible with proper authorization'
      ]
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Tenant-Host Requirements',
      description: 'Long-term tenants can legally host STRs in Vancouver, but face higher barriers. You must provide written landlord consent using the City\'s official form, plus formal Strata Council authorization if in a condo building.',
      details: [
        'Written landlord consent form required (City-provided)',
        'Strata Council authorization letter mandatory',
        'Proof of residency (driver\'s license, utility bills, tax docs)',
        'Application denied without proper authorization'
      ]
    },
    {
      icon: 'ri-government-line',
      title: 'Double Registry Mandatory',
      description: 'BC\'s 2026 Provincial Registry is fully active. All Vancouver STRs need both a Provincial Registry Number AND a City Business License. Platforms automatically de-list properties missing either verified number.',
      details: [
        'Provincial Registry Number required first',
        'City Business License: $1,108/year + $77 application fee',
        'Both numbers must display in all listings',
        'Automatic de-listing without valid numbers'
      ]
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Fire Safety Plan Required',
      description: 'All Vancouver STRs must submit a Fire Safety Plan including visual floor plan and certified fire extinguishers. Government ID and utility bills required for eligibility verification.',
      details: [
        'Visual floor plan showing exits',
        'Certified fire extinguishers required',
        'Government-issued ID mandatory',
        'Utility bills for residence proof'
      ]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Vancouver's 2026 Principal Residence Requirements
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Vancouver enforces BC's Provincial Short-Term Rental Accommodations Act with strict principal residence rules. Both property owners and long-term tenants can host, provided they meet all authorization requirements. The Provincial Registry is fully active in 2026.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {rules.map((rule, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-[#882629]/10 rounded-xl mb-6">
                <i className={`${rule.icon} text-3xl text-[#882629]`}></i>
              </div>
              <h3 className="text-2xl font-semibold text-[#505A5A] mb-4">
                {rule.title}
              </h3>
              <p className="text-base text-[#505A5A]/70 mb-6 leading-relaxed">
                {rule.description}
              </p>
              <ul className="space-y-3">
                {rule.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#882629] text-lg mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-[#505A5A]/80">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white border-l-4 border-[#882629] rounded-lg p-8 shadow-md">
          <div className="flex items-start gap-4">
            <i className="ri-alert-line text-3xl text-[#882629] flex-shrink-0"></i>
            <div>
              <h4 className="text-xl font-semibold text-[#505A5A] mb-3">
                Important: Tenant-Hosts Must Obtain Dual Authorization
              </h4>
              <p className="text-base text-[#505A5A]/70 leading-relaxed mb-4">
                If you're renting your unit, you cannot host STRs without both landlord consent and Strata Council approval. The City provides an official "Landlord Consent Form" that must be signed and uploaded during your application. Even with landlord permission, if your building's strata bylaws prohibit short-term rentals, the city will deny your license.
              </p>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                <strong>2026 Double Registry:</strong> You need both a BC Provincial Registration Number and a Vancouver Business License. Both numbers must be displayed on all platform listings. Platforms automatically remove listings missing either verified number.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
